import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import Unauthorized from "./Unauthorized";
import HeaderWithUserDetails from "./HeaderWithUserDetails";
import ChatDisableModal from "./ChatDisableModal";
import EmptyChannelMessage from "./EmptyChannelMessage";
import {
  Chat,
  Channel,
  MessageInput,
  Window,
  Thread,
  LoadingIndicator,
  useMessageInputContext,
  TypingIndicator,
  VirtualizedMessageList,
} from "stream-chat-react";
import { EmojiPicker } from "stream-chat-react/emojis";
import CustomDateSeparator from "./CustomDateSeparator";
import { useAppSettings } from "../context/AppSettingsContext";

import SidebarWithSearch from "./SidebarWithSearch";
import "stream-chat-react/dist/css/v2/index.css"; // Updated import path
import "stream-chat-react/css/v2/emoji-mart.css";
import CustomChannelHeader from "./CustomChannelHeader";
import ThreadCustomMessage from "./ThreadCustomMessage";
import CustomThreadHeader from "./CustomThreadHeader";
import "../styles/Chat.css";
import CustomMessage from "./CustomMessage";
import CustomMessageTimestamp from "./CustomMessageTimestamp";
import CustomAvatar from "./CustomAvatar";
import { useAuth } from "../context/AuthContext";
const customReactionOptions = [
  {
    name: "Thumbs up",
    type: "+1",
    Component: () => <>👍</>,
  },
  {
    name: "Thumbs down",
    type: "-1",
    Component: () => <>👎</>,
  },
  {
    name: "Heart",
    type: "heart",
    Component: () => <span style={{ color: "red" }}>❤️</span>,
  },
  {
    name: "LOL",
    type: "lol",
    Component: () => (
      <span style={{ color: "blue", fontWeight: "bold" }}>😂</span>
    ),
  },
];

const CustomEmojiPicker = () => {
  const { insertText } = useMessageInputContext();

  return (
    <EmojiPicker
      onSelect={(emoji) => insertText(emoji.native)} // Insert emoji into the input
    />
  );
};

const ChatPage = ({ token }) => {  
  const { authData } = useAuth(); // Access the context data
  const { clientId, eventId, accountId1, accountId2 } = authData;
 
  const [activeChannel, setActiveChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [chatError, setChatError] = useState(null); // Default is null, meaning no error

  const { chatClient, tokens } = useAppSettings();
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility
  const [userDetails, setUserDetails] = useState({
    primary: null,
    secondary: null,
  });
  // State to manage primary and secondary user IDs
  const [userIds, setUserIds] = useState({
    primary: `${eventId}_${clientId}_${accountId1}`,
    secondary: accountId2 ? `${eventId}_${clientId}_${accountId2}` : null, // Handle optional accountId2
  });
  // Add this reusable method for creating and initializing a new channel
  const createAndInitializeChannel = async (
    primaryUserId,
    secondaryUserId,
    team
  ) => {
    try {
      setLoading(true);
      const newChannel = chatClient.channel("messaging", {
        members: [primaryUserId, secondaryUserId],
        team,
      });

      await newChannel.create();
      await newChannel.watch({ watch: true, state: true });

      await fetchUserDetails([primaryUserId, secondaryUserId]);

      chatClient.dispatchEvent({
        type: "channel.created.custom",
        channel: newChannel,
      });

      setActiveChannel(newChannel);
      setLoading(false);
    } catch (err) {
      setChatError(err.message || "Error creating channel!");
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!clientId || !eventId || !accountId1) {
      setLoading(false); // Stop loading if parameters are missing
      setError(true);
      return;
    }
    setLoading(true);
    const checkUserShareStatus = async () => {
      try {
        if (!chatClient) {
          setLoading(false); // Stop loading if parameters are missing
          return;
        }
        if (!chatClient.user) {
          await chatClient.connectUser(
            { id: userIds.primary },
            tokens[`_stream_${token}`]
          );
        }
        const usersToCheck = [userIds.primary];
        if (userIds.secondary) usersToCheck.push(userIds.secondary);

        const { primaryUser, secondaryUser } = await fetchUserDetails(
          usersToCheck
        );
        if (primaryUser && !primaryUser.share) {
          setIsModalOpen(true);
          return;
        }

        if (secondaryUser && !secondaryUser.share) {
          setIsModalOpen(true);
          return;
        }

        // Proceed with channel initialization if both users have share = true
        await initializeChat();
      } catch (err) {
        setLoading(false);
        setError(true);
      }
    };

    const initializeChat = async () => {
      try {
        // Set default channel from URL
        let defaultChannel;
        const team = chatClient.user?.teams ? chatClient.user.teams[0] : "";
        const sort = [{ last_message_at: -1 }];
        const filters = userIds.secondary
          ? {
              type: "messaging",
              team: { $eq: team },
              member_count: { $eq: 2 },
              members: { $eq: [userIds.primary, userIds.secondary] },
            }
          : {
              team: { $eq: team },
              type: "messaging",
              members: { $in: [userIds.primary] },
              member_count: { $eq: 2 },
            };

        // Query for an existing channel
        const channels = await chatClient.queryChannels(filters, sort, {
          watch: true, // this is the default
          state: true,
        });
        if (userIds.secondary && channels.length > 0) {
          const matchedChannel = channels.find(
            (channel) => channel.state.members[userIds.secondary]
          );

          defaultChannel = matchedChannel || channels[0];
        } else if (userIds.primary && userIds.secondary) {
          await createAndInitializeChannel(
            userIds.primary,
            userIds.secondary,
            team
          );
          return;
        }

        if (defaultChannel) {
          await defaultChannel.watch({ watch: true, state: true });
          setActiveChannel(defaultChannel);
        }
        setLoading(false);

        // WebSocket listeners
        chatClient.on("connection.error", async () => {
          await new Promise((resolve) => setTimeout(resolve, 3000));
          try {
            await chatClient.disconnectUser();
            setTimeout(() => {
              chatClient.connectUser(
                { id: userIds.primary },
                tokens[userIds.primary]
              );
            }, 5000); // wait 5 seconds
          } catch (err) {
            setChatError(err.message || "Stream Chat Web Socket Failed!");
          }
        });

        chatClient.on("connection.recovered", () => {
          toast.success("🔁 Connection recovered automatically"); // Show success toast
        });
        return () => {};
      } catch (err) {
        setChatError(err.message || "Error initializing chat!");
        setError(true);
        setLoading(false);
      }
    };

    checkUserShareStatus();
  }, [clientId, eventId, accountId1, accountId2]);
  const closeModal = () => {
    setIsModalOpen(false);
  };
  const fetchUserDetails = async (userIdsToFetch) => {
    try {
      const response = await chatClient.queryUsers(
        { id: { $in: userIdsToFetch } },
        {},
        {}
      );

      const primaryUser = response.users.find((u) => u.id === userIds.primary);
      const secondaryUser = response.users.find(
        (u) => u.id === userIdsToFetch[1]
      );

      setUserDetails({
        primary: primaryUser || null,
        secondary: secondaryUser || null,
      });

      return { primaryUser, secondaryUser };
    } catch (err) {
      setChatError(err.message);
    }
  };

  const userIdsRef = useRef(userIds);

  useEffect(() => {
    userIdsRef.current = userIds;
  }, [userIds]);

  const handleUserSelect = useCallback(
    (selectedUserId) => {
      const currentUserIds = userIdsRef.current;
      if (!chatClient || selectedUserId === currentUserIds.primary) return;

      // Set secondary immediately (sync)
      setUserIds((prev) => ({
        ...prev,
        secondary: selectedUserId,
      }));
      // Continue the async flow separately
      (async () => {
        const primary = currentUserIds.primary;
        const team = chatClient.user?.teams?.[0] || "";

        const newChannel = chatClient.channel("messaging", {
          members: [primary, selectedUserId],
          team,
        });

        await newChannel.create();
        await newChannel.watch({ watch: true, state: true });

        await fetchUserDetails([primary, selectedUserId]);

        chatClient.dispatchEvent({
          type: "channel.created.custom",
          channel: newChannel,
        });

        setActiveChannel(newChannel);
      })();
    },
    [chatClient]
  );

  const loadMoreMessages = async () => {
    if (!activeChannel || loadingMore || !hasMore) return;

    setLoadingMore(true);
    const oldestMessage = activeChannel.state.messages[0];

    if (!oldestMessage) {
      setLoadingMore(false);
      return;
    }

    const response = await activeChannel.query({
      messages: {
        limit: 20,
        id_lt: oldestMessage.id,
      },
    });

    if (response.messages.length === 0) {
      setHasMore(false); // No more messages to load
    }

    setLoadingMore(false);
    // Mark messages as read when the user scrolls to the bottom
    if (activeChannel.state.isUpToDate) {
      await activeChannel.markRead();
    }
  };
  if (!clientId || !eventId || !userIds.primary) {
    return <Unauthorized />;
  }
  if (error)
    return (
      <Unauthorized errorMessage="Failed to connect to the chat server. Please try again later." />
    );
  if (chatError) return <Unauthorized errorMessage={chatError} />;
  if (loading || !chatClient) return <LoadingIndicator />;

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        width: "100vw",
        flexDirection: "column",
       
      }}
    >
      <HeaderWithUserDetails currentUser={chatClient.user} />
      <div style={{ flex: "1", display: "flex" }}>
        <div style={{ flex: "0 0 300px", borderRight: "1px solid #ddd" }}>
          <MemoizedSidebarWithSearch
            chatClient={chatClient}
            onUserSelect={handleUserSelect}
            currentUserId={userIds.primary}
            chatUserId={userIds.secondary}
          />
        </div>

        <div style={{ flex: "1", display: "flex", flexDirection: "column" }}>
          <Chat client={chatClient} theme="messaging light">
            {activeChannel ? (
              <Channel
                TypingIndicator={TypingIndicator}
                giphyVersion="fixed_height_small"
                channel={activeChannel}
                EmojiPicker={CustomEmojiPicker}
                ThreadMessage={ThreadCustomMessage}
                ThreadHeader={CustomThreadHeader}
                VirtualMessage={CustomMessage}
                MessageTimestamp={CustomMessageTimestamp}
                reactionOptions={customReactionOptions}
                Avatar={CustomAvatar}
              >
                <Window>
                  <CustomChannelHeader />
                  <VirtualizedMessageList
                    stickToBottomScrollBehavior="auto" // Automatically scroll to the bottom
                    CustomDateSeparator={CustomDateSeparator} // Custom date separator
                    onYReachStart={loadMoreMessages} // Load more messages when scrolling to the top
                    LoadingIndicator={() =>
                      loadingMore && (
                        <div style={{ textAlign: "center", padding: "10px" }}>
                          <LoadingIndicator />{" "}
                          {/* Built-in loading indicator */}
                        </div>
                      )
                    }
                  />

                  <TypingIndicator />
                  <MessageInput
                    additionalTextareaProps={{
                      placeholder: "Type your message...",
                    }}
                  />
                </Window>
                <Thread />
              </Channel>
            ) : (
              <EmptyChannelMessage/>
            )}
          </Chat>
        </div>
        <ChatDisableModal
          isOpen={isModalOpen}
          toggle={closeModal}
          userDetails={userDetails} // Pass userDetails here
          disableClose={!userIds.secondary}
        />
      </div>
    </div>
  );
};

export default ChatPage;
// ✅ Memoized version of SidebarWithSearch
const MemoizedSidebarWithSearch = React.memo(SidebarWithSearch);
